import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import {
  MsalService,
  MsalBroadcastService
} from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ThemeService } from './core/theme/theme.service';
import { AuthService } from './core/auth/auth.service';

/**
 * Root-Komponente der Production-Planner-Anwendung.
 *
 * Verantwortlich für:
 * - Initialisierung des MSAL-Redirect-Flows nach dem Azure-AD-Login
 * - Initialisierung des ThemeService (Dark/Light/Normal + Tenant-Branding)
 * - Bereitstellung des `<router-outlet>` für alle Feature-Module
 *
 * Das eigentliche App-Shell-Layout (Navbar, Sidebar, Content) wird von
 * `MainLayoutComponent` bereitgestellt, die über das Routing eingebunden wird.
 * Die Login-Logik ist in `LoginComponent` ausgelagert.
 */
@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {

  /** Subject zum sauberen Beenden aller aktiven RxJS-Subscriptions. */
  private readonly destroy$ = new Subject<void>();

  constructor(
    private msalService: MsalService,
    private broadcastService: MsalBroadcastService,
    private themeService: ThemeService,
    private authService: AuthService
  ) {}

  /**
   * Initialisiert den MSAL-Redirect-Handler und den ThemeService.
   * Der Redirect-Handler verarbeitet den Rücksprung von Azure AD nach dem Login.
   */
  ngOnInit(): void {
    // Theme aus localStorage wiederherstellen
    this.themeService.init();

    // MSAL-Redirect verarbeiten
    this.msalService.handleRedirectObservable().subscribe({
      error: (fehler) => console.error('[App] MSAL Redirect-Fehler:', fehler)
    });

    // MSAL-Interaktionsstatus überwachen – sobald keine Interaktion mehr läuft,
    // User-Profil vorladen damit Guards nicht blockieren müssen
    this.broadcastService.inProgress$
      .pipe(
        filter(status => status === InteractionStatus.None),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.authService.isLoggedIn()) {
          this.authService.ladeUser();
        }
      });
  }

  /**
   * Gibt alle RxJS-Subscriptions frei wenn die Root-Komponente zerstört wird.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
